// src/pages/GamesHubPage.jsx

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import {
  gamesHubFilters,
  gamesHubItems,
  difficultyClassMap,
} from "../data/gamesHubData";

const GamesHubPage = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState("all");

  const games = gamesHubItems;
  const filters = gamesHubFilters;

  const filteredGames = useMemo(() => {
    if (activeFilter === "all") return games;
    return games.filter((game) => game.category === activeFilter);
  }, [activeFilter, games]);

  const difficultyStyle = (difficultyStyleKey) =>
    difficultyClassMap[difficultyStyleKey] ?? difficultyClassMap.challenging;

  return (
    <div className="min-h-screen flex flex-col text-[#2b2118] bg-[#fff7ed]">
      <Helmet>
        <title>{t("meta.gamesTitle")}</title>
        <meta name="description" content={t("meta.gamesDescription")} />
      </Helmet>

      <SiteHeader />

      <main className="flex-grow overflow-hidden">
        {/* Hero Section */}
        <section className="relative px-4 pt-10 pb-12 md:pt-14 md:pb-16 bg-gradient-to-br from-amber-300 via-orange-200 to-rose-200 rounded-b-[2.5rem] shadow-sm">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-8 left-6 w-20 h-20 bg-white/40 rounded-full blur-sm" />
            <div className="absolute top-24 right-10 w-28 h-28 bg-white/30 rounded-full blur-sm" />
            <div className="absolute bottom-6 left-1/3 w-16 h-16 bg-yellow-100/60 rounded-full blur-sm" />
            <div className="absolute -bottom-10 -right-8 w-40 h-40 bg-white/30 rounded-full" />
            <div className="absolute -top-12 -left-12 w-44 h-44 bg-white/20 rounded-full" />
          </div>

          <div className="relative max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-8 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-white/75 backdrop-blur-sm border border-white/80 rounded-full px-4 py-2 shadow-sm mb-5">
                  <span className="text-xl">🎮</span>
                  <span className="text-sm md:text-base font-bold text-amber-900">
                    {t("gamesHub.badge")}
                  </span>
                </div>

                <h1 className="text-4xl md:text-6xl font-black leading-tight text-amber-950 drop-shadow-sm">
                  {t("gamesHub.hero.titleLine1")}
                  <span className="block text-orange-700">{t("gamesHub.hero.titleLine2")}</span>
                </h1>

                <p className="mt-4 text-base md:text-xl text-amber-950/80 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
                  {t("gamesHub.hero.description")}
                </p>

                <div className="mt-6 flex flex-wrap justify-center lg:justify-start gap-3">
                  <div className="bg-white/80 border border-white rounded-2xl px-4 py-3 shadow-sm">
                    <p className="text-2xl font-black text-emerald-700">{t("gamesHub.stats.kidFriendlyValue")}</p>
                    <p className="text-xs font-bold text-stone-600">{t("gamesHub.stats.kidFriendlyLabel")}</p>
                  </div>
                  <div className="bg-white/80 border border-white rounded-2xl px-4 py-3 shadow-sm">
                    <p className="text-2xl font-black text-purple-700">{t("gamesHub.stats.funValue")}</p>
                    <p className="text-xs font-bold text-stone-600">{t("gamesHub.stats.funLabel")}</p>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block">
                <div className="relative bg-white/70 backdrop-blur-sm border border-white rounded-[2rem] p-6 shadow-xl rotate-2">
                  <div className="absolute -top-5 -left-5 w-16 h-16 bg-yellow-300 rounded-3xl flex items-center justify-center text-3xl shadow-lg -rotate-12">
                    🌈
                  </div>
                  <div className="absolute -bottom-5 -right-5 w-16 h-16 bg-rose-300 rounded-3xl flex items-center justify-center text-3xl shadow-lg rotate-12">
                    🌹
                  </div>

                  <div className="bg-white rounded-[1.5rem] p-5 shadow-sm">
                    <div className="text-center">
                      <div className="text-7xl mb-3">📚</div>
                      <h2 className="text-2xl font-black text-amber-900">
                        {t("gamesHub.preview.title")}
                      </h2>
                      <p className="text-stone-600 font-medium mt-2">
                        {t("gamesHub.preview.description")}
                      </p>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-3">
                      {["🧹", "✍️", "📖"].map((icon, index) => (
                        <div
                          key={index}
                          className="aspect-square rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-4xl"
                        >
                          {icon}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="max-w-6xl mx-auto px-4 mt-8">
          <div className="bg-white rounded-[1.75rem] border border-orange-100 shadow-sm p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-black text-amber-950">
                  {t("gamesHub.filters.title")}
                </h2>
                <p className="text-sm text-stone-500 font-medium">
                  {t("gamesHub.filters.description")}
                </p>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0">
                {filters.map((filter) => {
                  const isActive = activeFilter === filter.id;

                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => setActiveFilter(filter.id)}
                      className={[
                        "shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm font-black border transition-all active:scale-95",
                        isActive
                          ? "bg-amber-700 text-white border-amber-700 shadow-md"
                          : "bg-amber-50 text-amber-900 border-amber-100 hover:bg-amber-100",
                      ].join(" ")}
                    >
                      <span>{filter.icon}</span>
                      <span>{t(filter.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Games Grid */}
        <section className="max-w-6xl mx-auto px-4 py-8 md:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
            {filteredGames.map((game) => (
              <article
                key={game.id}
                className={[
                  "group relative rounded-[2rem] border-2 bg-gradient-to-br p-4 shadow-sm transition-all duration-300",
                  "hover:-translate-y-1 hover:shadow-xl",
                  game.cardBg,
                  game.border,
                ].join(" ")}
              >
                <div className="absolute top-4 right-4">
                  <span
                    className={[
                      "inline-flex items-center rounded-full border px-3 py-1 text-xs font-black",
                      difficultyStyle(game.difficultyStyle),
                    ].join(" ")}
                  >
                    {t(game.difficultyKey)}
                  </span>
                </div>

                <div className="bg-white/85 backdrop-blur-sm rounded-[1.5rem] border border-white p-5 h-full flex flex-col">
                  <div className="flex items-start gap-4 pr-16">
                    <div
                      className={[
                        "w-16 h-16 rounded-3xl flex items-center justify-center text-4xl shadow-sm border border-white",
                        game.emojiBg,
                      ].join(" ")}
                    >
                      {game.icon}
                    </div>

                    <div className="min-w-0">
                      <p
                        className={[
                          "inline-flex rounded-full px-3 py-1 text-xs font-black mb-2",
                          game.emojiBg,
                          game.text,
                        ].join(" ")}
                      >
                        {t(game.typeKey)}
                      </p>
                      <h3 className="text-xl md:text-2xl font-black text-stone-900 leading-tight">
                        {t(game.titleKey)}
                      </h3>
                      <p className="text-sm text-stone-500 font-semibold mt-1">
                        {t(game.subtitleKey)}
                      </p>
                    </div>
                  </div>

                  <p className="mt-5 text-[15px] leading-relaxed text-stone-700 font-medium flex-grow">
                    {t(game.descriptionKey)}
                  </p>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-stone-50 border border-stone-100 p-3">
                      <p className="text-xs font-black text-stone-400">{t("gamesHub.card.playTime")}</p>
                      <p className="text-sm font-black text-stone-700 mt-1">
                        ⏱️ {t(game.timeKey)}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-stone-50 border border-stone-100 p-3">
                      <p className="text-xs font-black text-stone-400">{t("gamesHub.card.skill")}</p>
                      <p className="text-sm font-black text-stone-700 mt-1">
                        ⭐ {t(game.skillKey)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-100 p-4">
                    <p className="text-xs font-black text-amber-700 mb-1">
                      💡 {t("gamesHub.card.howToPlay")}
                    </p>
                    <p className="text-sm leading-relaxed text-amber-950 font-medium">
                      {t(game.instructionKey)}
                    </p>
                  </div>

                  <Link
                    to={game.link}
                    className={[
                      "mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-4",
                      "text-white text-lg font-black shadow-md transition-all",
                      "group-hover:shadow-lg active:scale-95",
                      game.button,
                    ].join(" ")}
                    aria-label={t("gamesHub.card.playAria", {
                      title: t(game.titleKey),
                    })}
                  >
                    <span>{t("gamesHub.card.playNow")}</span>
                    <span className="text-2xl">▶</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {filteredGames.length === 0 && (
            <div className="text-center bg-white rounded-[2rem] border border-amber-100 p-10 shadow-sm">
              <div className="text-6xl mb-4">🔎</div>
              <h3 className="text-2xl font-black text-amber-900">
                {t("gamesHub.empty.title")}
              </h3>
              <p className="text-stone-500 mt-2">
                {t("gamesHub.empty.description")}
              </p>
              <button
                type="button"
                onClick={() => setActiveFilter("all")}
                className="mt-5 bg-amber-600 hover:bg-amber-700 text-white font-black px-6 py-3 rounded-2xl transition active:scale-95"
              >
                {t("gamesHub.empty.button")}
              </button>
            </div>
          )}
        </section>

        {/* Bottom Friendly Note */}
        <section className="max-w-6xl mx-auto px-4 pb-12">
          <div className="rounded-[2rem] bg-gradient-to-r from-emerald-100 via-sky-100 to-purple-100 border border-white p-5 md:p-6 shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
              <div className="w-16 h-16 rounded-3xl bg-white flex items-center justify-center text-4xl shadow-sm">
                🧒
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-black text-stone-900">
                  {t("gamesHub.note.title")}
                </h2>
                <p className="text-stone-600 font-medium mt-1 leading-relaxed">
                  {t("gamesHub.note.description")}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GamesHubPage;